import { DirectionMapboxProfile } from '@/models/enums';

export interface UseDirectionReturn {
  distance: ComputedRef<number | null>;
  duration: ComputedRef<number | null>;
  coordinates: ComputedRef<Coordinates[]>;
}

interface Direction {
  distance: number | null;
  duration: number | null;
  coordinates: Coordinates[];
}

const emptyDirection: Direction = {
  distance: null,
  duration: null,
  coordinates: [],
};

/**
 * Returns the distance, duration and coordinates of a direction between two points
 */
export function useDirection(
  from: Ref<Coordinates | null>,
  to: Ref<Coordinates | null>,
  profile: DirectionMapboxProfile
): UseDirectionReturn {
  const api = useApi();

  const fromThrottled = refThrottled(from, 200, false);
  const toThrottled = refThrottled(to, 200, false);

  const distance = computed(() => direction.value.distance);
  const duration = computed(() => direction.value.duration);
  const coordinates = computed(() => direction.value.coordinates);

  const direction = computedAsync<{
    distance: number | null;
    duration: number | null;
    coordinates: Coordinates[];
  }>(async () => {
    if (
      !fromThrottled.value ||
      !toThrottled.value ||
      !areCoordinatesValid(fromThrottled.value) ||
      !areCoordinatesValid(toThrottled.value)
    ) {
      return emptyDirection;
    }

    const { data: directionData, error: directionError } = await api.post(
      `/client/entities/direction`,
      {
        body: {
          from: fromThrottled.value,
          to: toThrottled.value,
          profile,
        },
      }
    );

    if (directionError.value) {
      useErrorAlert(directionError.value);
      return emptyDirection;
    }

    const coordinates = directionData?.value?.direction.coordinates ?? [];
    const safeCoordinates = coordinates.length
      ? coordinates ?? [fromThrottled.value, toThrottled.value]
      : [fromThrottled.value, toThrottled.value];

    return {
      distance: directionData?.value?.direction.distance ?? null,
      duration: directionData?.value?.direction.duration ?? null,
      coordinates: safeCoordinates,
    };
  }, emptyDirection);

  function areCoordinatesValid(coordinates: Coordinates): boolean {
    return (
      coordinates &&
      coordinates.every(coordinate => coordinate && coordinate !== Infinity)
    );
  }

  return {
    distance,
    duration,
    coordinates,
  };
}
